import type { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, throwError } from 'rxjs';
import { errorCode, httpStatus } from '../../shared/utils/http-error.util';

// A magic-link session is limited to the school (or class) it entered from;
// reading outside it answers 403 with this code (docs/auth/entry-scope-enforcement.md).
const OUT_OF_ENTRY_SCOPE = 'OUT_OF_ENTRY_SCOPE';
// Reads whose 403 is expected and handled by the screen: the class list is for school
// members, so a class-entry session (homeroom teacher) gets 403 and shows no class names;
// the ledger download is admin only and the download button says why it failed.
const QUIET_FORBIDDEN_READS = [
  /\/orgs\/[^/]+\/teams\/?(?:\?.*)?$/,
  /\/orgs\/[^/]+\/point-entries\/export\/?(?:\?.*)?$/,
];

/**
 * Tells the user when a screen could not load because of a 403. Screens handle
 * their own save errors, and outages show the shell's full-page error
 * (`spinoffAuthInterceptor`), so this only covers reads (GET).
 */
export const conductErrorNoticeInterceptor: HttpInterceptorFn = (req, next) => {
  const snackBar = inject(MatSnackBar);

  return next(req).pipe(
    catchError((error: unknown) => {
      const forbidden =
        req.method === 'GET' &&
        !QUIET_FORBIDDEN_READS.some((read) => read.test(req.url)) &&
        httpStatus(error) === 403;

      if (forbidden) {
        snackBar.open(
          forbiddenMessage(error),
          $localize`:@@conductNoticeOk:확인`,
          {
            duration: 6000,
          },
        );
      }

      return throwError(() => error);
    }),
  );
};

function forbiddenMessage(error: unknown) {
  if (errorCode(error) === OUT_OF_ENTRY_SCOPE) {
    return $localize`:@@conductOutOfEntryScope:학교종이에서 들어온 학교·반 밖의 정보는 볼 수 없습니다.`;
  }

  return $localize`:@@conductForbidden:이 정보를 볼 권한이 없습니다.`;
}
